"use client";

import { useMemo, useState } from "react";
import { Fuel, MapPin, Search, Tag } from "lucide-react";
import type { GasStation, GasStationFilters } from "@/types/database";
import { defaultGasStationFilters, filterGasStations, uniqueValues } from "@/lib/utils";
import { GasStationCard } from "@/components/gas-stations/GasStationCard";
import { Input, Select } from "@/components/ui/Input";
import { Workspace, WorkspaceSection, FieldLabel } from "@/components/ui/Workspace";
import { ChipGroup } from "@/components/ui/FilterPanel";
import { DualRange } from "@/components/ui/RangeSlider";

export function GasStationsExplorer({ stations }: { stations: GasStation[] }) {
  const [filters, setFilters] = useState<GasStationFilters>(defaultGasStationFilters());

  const brands = useMemo(() => uniqueValues(stations, (s) => s.name), [stations]);
  const parishes = useMemo(() => uniqueValues(stations, (s) => s.parish), [stations]);
  const filtered = useMemo(() => filterGasStations(stations, filters), [stations, filters]);

  const prices = stations.map((s) => s.fuelPrice_87 ?? 0).filter((p) => p > 0);
  const priceMinBound = prices.length ? Math.floor(Math.min(...prices)) : 150;
  const priceMaxBound = prices.length ? Math.ceil(Math.max(...prices)) : 250;
  const [rangeLow, setRangeLow] = useState(priceMinBound);
  const [rangeHigh, setRangeHigh] = useState(priceMaxBound);

  function applyRange(low: number, high: number) {
    setRangeLow(low);
    setRangeHigh(high);
    setFilters((f) => ({
      ...f,
      priceMin: low > priceMinBound ? String(low) : "",
      priceMax: high < priceMaxBound ? String(high) : "",
    }));
  }

  function resetAll() {
    setFilters(defaultGasStationFilters());
    setRangeLow(priceMinBound);
    setRangeHigh(priceMaxBound);
  }

  return (
    <Workspace>
      <WorkspaceSection className="border-b border-border p-4 sm:p-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <FieldLabel icon={<Search className="h-3 w-3" />}>Search</FieldLabel>
            <Input placeholder="Name, address, parish…" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} />
          </div>
          <div>
            <FieldLabel icon={<Tag className="h-3 w-3" />}>Brand</FieldLabel>
            <Select value={filters.brand} onChange={(e) => setFilters((f) => ({ ...f, brand: e.target.value }))}>
              <option value="">All brands</option>
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </Select>
          </div>
          <div>
            <FieldLabel icon={<MapPin className="h-3 w-3" />}>Parish</FieldLabel>
            <Select value={filters.parish} onChange={(e) => setFilters((f) => ({ ...f, parish: e.target.value }))}>
              <option value="">All parishes</option>
              {parishes.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
          </div>
          <div>
            <FieldLabel icon={<Fuel className="h-3 w-3" />}>Coordinates</FieldLabel>
            <Select value={filters.hasCoords} onChange={(e) => setFilters((f) => ({ ...f, hasCoords: e.target.value }))}>
              <option value="">All stations</option>
              <option value="yes">Has coordinates</option>
              <option value="no">Missing coordinates</option>
            </Select>
          </div>
        </div>
      </WorkspaceSection>

      <WorkspaceSection border className="grid lg:grid-cols-[220px_1fr]">
        <aside className="border-b border-border p-4 lg:border-b-0 lg:border-r">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-bold">Filters</h3>
            <button type="button" onClick={resetAll} className="text-xs font-bold text-accent-link transition hover:text-foreground">
              Reset all
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <FieldLabel>Brand</FieldLabel>
              <Select value={filters.brand} onChange={(e) => setFilters((f) => ({ ...f, brand: e.target.value }))}>
                <option value="">All brands</option>
                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
              </Select>
            </div>

            <div>
              <FieldLabel>Parish</FieldLabel>
              <Select value={filters.parish} onChange={(e) => setFilters((f) => ({ ...f, parish: e.target.value }))}>
                <option value="">All parishes</option>
                {parishes.map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
            </div>

            <div>
              <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-muted">E-10 87 range</p>
              <DualRange
                min={priceMinBound}
                max={priceMaxBound}
                low={rangeLow}
                high={rangeHigh}
                onLowChange={(v) => applyRange(v, rangeHigh)}
                onHighChange={(v) => applyRange(rangeLow, v)}
                format={(v) => `$${v.toFixed(1)}`}
              />
            </div>

            <ChipGroup
              label="Map status"
              options={["Has coords", "Missing"]}
              value={filters.hasCoords === "yes" ? "Has coords" : filters.hasCoords === "no" ? "Missing" : ""}
              onChange={(v) =>
                setFilters((f) => ({
                  ...f,
                  hasCoords: v === "Has coords" ? "yes" : v === "Missing" ? "no" : "",
                }))
              }
            />
          </div>
        </aside>

        <section className="p-4">
          <p className="mb-5 text-sm text-muted">
            <span className="font-bold text-foreground">{filtered.length}</span> stations
          </p>

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <p className="font-bold">No matches</p>
              <p className="mt-1 text-sm text-muted">Adjust your filters to see results.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {filtered.map((station) => (
                <GasStationCard key={station.id} station={station} />
              ))}
            </div>
          )}
        </section>
      </WorkspaceSection>
    </Workspace>
  );
}
